type InputObject = {
    id: number;
    accepted_price_kwh: number;
    allocated_quantity_mw: number;
    auction_initiation_date: string;
    auction_initiation_time: string;
    auction_no: string;
    auction_result_date: string;
    auction_result_time: string;
    booking_accepted_price_kwh: number;
    total_count_of_sellers_who_participated_in_the_auction: number | null;
    booking_quantity_mw: number;
    buy_minimum_quantity_mw: number | null;
    buy_total_quantity_mw: number;
    buyer__name: string;
    delivery_end_date: string;
    delivery_end_time: string;
    delivery_start_date: string;
    delivery_start_time: string;
    duplicate: number;
    exchange_type__name: string;
    requisition_no: string;
    type__name: string;
};

type OutputObject = {
    date: string;
    month: number;
    exchange: string;
    type: string;
    slot?: number;
    total_volume_mwh: number;
    weighted_average_price: number;
    product: string;
};


export function ProcessAuctionData(inputData: InputObject[], startDate: string, endDate: string): OutputObject[] {
    const outputData: OutputObject[] = [];
    
    // Convert the provided start and end date strings to Date objects
    const filterStartDate = new Date(startDate);
    const filterEndDate = new Date(endDate);
    filterEndDate.setHours(23, 59, 59, 999); // Ensure end date includes the entire day

    for (const data of inputData) {
        // Parse the delivery start and end datetime
        const startDateTime = new Date(`${data.delivery_start_date}T${data.delivery_start_time}`);
        const endDateTime = new Date(`${data.delivery_end_date}T${data.delivery_end_time}`);

        // Adjust end time if it is "23:59:59" to "24:00:00" (next day)
        if (data.delivery_end_time === "23:59:59") {
            endDateTime.setSeconds(endDateTime.getSeconds() + 1);
        }

        // Loop through each day between the start and end dates
        let currentDateTime = new Date(startDateTime);

        while (currentDateTime <= endDateTime) {
            // Check if the current date is within the provided range
            if (currentDateTime >= filterStartDate && currentDateTime <= filterEndDate) {
                const nextDate = new Date(currentDateTime);
                nextDate.setDate(currentDateTime.getDate() + 1);
                nextDate.setHours(0, 0, 0, 0);

                // Calculate hours for this day
                const dayEndTime = nextDate < endDateTime ? nextDate : endDateTime;
                const hoursInDay = (dayEndTime.getTime() - currentDateTime.getTime()) / (1000 * 60 * 60);

                // Calculate the total volume for this day
                const totalVolumeMwh = data.allocated_quantity_mw * hoursInDay;

                outputData.push({
                    date: currentDateTime.toISOString().split('T')[0],
                    month: currentDateTime.getMonth() + 1,
                    exchange: data.exchange_type__name,
                    type: "RA",
                    total_volume_mwh: totalVolumeMwh,
                    weighted_average_price: data.accepted_price_kwh,
                    product: "RA"
                });
            }

            // Move to the next day
            currentDateTime.setDate(currentDateTime.getDate() + 1);
            currentDateTime.setHours(0, 0, 0, 0);
        }
    }

    // Sort by date
    outputData.sort((a, b) => a.date.localeCompare(b.date));

    return outputData;
}


export function ProcessAuctionDataBySlot(inputData: InputObject[], startDate: string, endDate: string): OutputObject[] {
    const outputData: OutputObject[] = [];

    // Convert the provided start and end date strings to timestamps (milliseconds since Unix epoch)
    const filterStartTimestamp = new Date(startDate).getTime();
    const filterEndTimestamp = new Date(endDate).setHours(23, 59, 59, 999); // End date inclusive

    for (const data of inputData) {
        // Convert delivery start and end datetime to timestamps
        const startTimestamp = new Date(`${data.delivery_start_date}T${data.delivery_start_time}`).getTime();
        let endTimestamp = new Date(`${data.delivery_end_date}T${data.delivery_end_time}`).getTime();

        // Adjust end time if it is "23:59:59" to "24:00:00" (next day)
        if (data.delivery_end_time === "23:59:59") {
            endTimestamp += 1000; // Add one second
        }

        // Calculate volume per 15-minute slot
        const volumePerSlot = data.allocated_quantity_mw / 4;

        // Loop through 15-minute intervals within the provided date range
        let currentTimestamp = startTimestamp;

        while (currentTimestamp <= endTimestamp) {
            // Check if the current time is within the provided range
            if (currentTimestamp >= filterStartTimestamp && currentTimestamp <= filterEndTimestamp) {
                const currentDate = new Date(currentTimestamp);
                const slotNumber = currentDate.getHours() * 4 + Math.floor(currentDate.getMinutes() / 15) + 1;

                outputData.push({
                    date: currentDate.toISOString().split('T')[0],
                    month: currentDate.getMonth() + 1,
                    exchange: data.exchange_type__name,
                    type: "RA",
                    slot: slotNumber,
                    total_volume_mwh: volumePerSlot,
                    weighted_average_price: data.accepted_price_kwh,
                    product: "RA"
                });
            }

            // Increment by 15 minutes in milliseconds
            currentTimestamp += 15 * 60 * 1000;
        }
    }

    // Sort by date and slot
    outputData.sort((a, b) => {
        if (a.date === b.date) {
            return a.slot! - b.slot!;
        }
        return a.date.localeCompare(b.date);
    });

    return outputData;
}